// Inline curtain card — Open / Stop / Close right on the grid card (no window).
// Commands OPENCURTAIN / STOPCURTAIN / CLOSECURTAIN (matches the Flutter app).
//
// A press is a TIMED RUN; the clock lives in core/devices/curtainRunner, which
// is module-level — so the countdown here and the countdown on the device
// screen are the same run, not two timers racing over one motor.

import React, { useState } from 'react';
import {
  Card, Box, Typography, Button, LinearProgress, IconButton, Tooltip,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import StopIcon from '@mui/icons-material/Stop';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TimerIcon from '@mui/icons-material/Timer';
import useCurtain from '../../../core/hooks/useCurtain';
import CurtainTimesDialog from '../../shared/CurtainTimesDialog';
import DeviceStatusDot from './DeviceStatusDot';
import DeviceCardMenu from './DeviceCardMenu';

// Icon above a wrapping label, so three buttons fit a 230 px column without
// either the words or the arrows being dropped. `minWidth: 0` is what lets a
// grid cell shrink below its content — without it the row overflows instead of
// wrapping, which is the same clipping this layout exists to fix.
const STEP_SX = {
  flexDirection: 'column',
  gap: 0.15,
  px: 0.5,
  py: 0.6,
  minWidth: 0,
  lineHeight: 1.1,
  fontSize: 10.5,
};
const ICON_SX = { fontSize: 15 };

export default function CurtainControlCard({ device, subtitle }) {
  const uno = device.UniqueNumber;
  const curtain = useCurtain(device);
  const [editTimes, setEditTimes] = useState(false);

  return (
    <Card sx={{ p: 1.25, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.75, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.2 }}>{subtitle}</Typography>
          )}
          <Typography fontWeight={700} color="text.primary" noWrap>{device.DeviceName || uno}</Typography>
        </Box>
        <Tooltip title={`Run time — open ${curtain.times.openSeconds}s, close ${curtain.times.closeSeconds}s`}>
          <IconButton size="small" onClick={() => setEditTimes(true)}>
            <TimerIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <DeviceStatusDot device={device} />
        <DeviceCardMenu device={device} />
      </Box>

      {/* Reserved height: a card that grows when the motor starts shoves every
          other card in the room grid down a row.
          Idle, the reserved line carries the configured run times rather than
          sitting blank — the space is already spent, and it is the number that
          explains how long a press will last. */}
      <Box sx={{ minHeight: 20 }}>
        {curtain.moving ? (
          <>
            <LinearProgress variant="determinate" value={curtain.progress} sx={{ height: 4, borderRadius: 3 }} />
            <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {`${curtain.opening ? 'Opening' : 'Closing'} — ${curtain.remainingSeconds}s`}
            </Typography>
          </>
        ) : (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.4 }}>
            {curtain.times.singleOutput
              ? `Runs ${curtain.times.openSeconds}s`
              : `Open ${curtain.times.openSeconds}s · Close ${curtain.times.closeSeconds}s`}
          </Typography>
        )}
      </Box>

      {/*
        ⚠️ THREE ACROSS, NOT STACKED. Stacked, the card was ~120 px of buttons —
        taller than the column it sits in on `/device-admin`, where it shares a
        fixed-height grid cell with the device facts. The overflow was CLIPPED,
        not scrolled, so the controls were simply not on screen: the one thing
        the panel exists for was the thing that got cut.

        Three across is ~44 px, and it is also the layout the mobile screen and
        the wall panel already use — so the same control now reads the same way
        everywhere instead of being a column here and a row there.

        The label keeps its noun ("Open Curtain") and wraps to two lines rather
        than being shortened to "Open": on a wall panel the tile is captioned by
        the button's own text, but here the operator may be four devices deep in
        an admin screen and "Open" alone does not say what opens.
      */}
      <Box sx={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75, mt: 'auto',
      }}
      >
        {/* Disabled while its own direction runs — a repeat is a STOP at the
            device, not a restart. See curtainRunner rule 3. */}
        <Button
          variant="contained" size="small" sx={STEP_SX}
          disabled={curtain.opening} onClick={curtain.open}
        >
          <ArrowUpwardIcon sx={ICON_SX} />
          {curtain.labels.open}
        </Button>
        <Button
          variant={curtain.moving ? 'contained' : 'outlined'}
          color={curtain.moving ? 'warning' : 'primary'}
          size="small"
          onClick={curtain.stop}
          sx={curtain.moving ? STEP_SX : { ...STEP_SX, color: 'text.primary', borderColor: 'divider' }}
        >
          <StopIcon sx={ICON_SX} />
          {curtain.labels.stop}
        </Button>
        <Button
          variant="contained" size="small" sx={STEP_SX}
          disabled={curtain.closing} onClick={curtain.close}
        >
          <ArrowDownwardIcon sx={ICON_SX} />
          {curtain.labels.close}
        </Button>
      </Box>

      <CurtainTimesDialog
        open={editTimes}
        onClose={() => setEditTimes(false)}
        curtain={curtain}
        deviceName={device.DeviceName}
      />
    </Card>
  );
}
